import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";
import * as Sentry from "@sentry/react";

export function useCreateAdminAxiosInstance(baseURL: string) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const createAxiosInstance = useCallback(() => {
    const axiosInstance = axios.create({ baseURL });

    axiosInstance.interceptors.request.use(async (req) => {
      try {
        req.headers.set(
          "Authorization",
          `Bearer ${await getAccessTokenSilently()}`,
        );
        return req;
      } catch (error) {
        // getAccessTokenSilently throws when the cached/refresh token is no
        // longer usable (expired session, revoked consent, changed auth
        // config, ...). Left uncaught, this would abort the request before
        // it reaches the network with no visible error, silently rendering
        // admin lists as empty. Report it and force an interactive re-login
        // instead.
        Sentry.captureException(error);
        await loginWithRedirect({
          appState: {
            returnTo: `${window.location.pathname}${window.location.search}`,
          },
        });
        throw error;
      }
    });

    return axiosInstance;
  }, [getAccessTokenSilently, loginWithRedirect, baseURL]);

  return {
    createAxiosInstance,
  };
}
